<script lang="ts" setup>
import { useDebounceFn, useVModel } from '@vueuse/core'

interface IProps {
  modelValue: string
  type?: string
  placeholder?: string
  readonly?: boolean
  debounceTime?: number
  focusWhenMounted?: boolean
  name?: string
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: '',
  type: 'text',
  placeholder: '',
  readonly: false,
  debounceTime: 0
})

const emits = defineEmits([ 'update:modelValue', 'searchRedirect', 'enter', 'esc' ])

const vmInputValue = useVModel(props, 'modelValue')

const inputRef = ref<HTMLInputElement | null>(null)

const clearInput = () => {
  vmInputValue.value = ''
  emits('update:modelValue', '')
}

const handleInput = useDebounceFn((event: InputEvent) => {
  const eventTarget = event.target as HTMLInputElement
  vmInputValue.value = eventTarget.value
}, props.debounceTime)

onMounted(() => {
  if (inputRef.value && props.focusWhenMounted) {
    inputRef.value.focus()
  }
})
</script>

<template>
  <div
    class="input-container"
    :class="type"
  >
    <div
      v-if="$slots.prefix"
      class="shrink-0"
    >
      <slot name="prefix" />
    </div>
    <input
      v-model="vmInputValue"
      :name="name"
      ref="inputRef"
      class="default-input"
      :type="type"
      :placeholder="placeholder"
      :readonly="readonly"
      @input="handleInput"
      @keydown.enter="emits('enter')"
      @keydown.esc="emits('esc')"
    >
    <button
      v-if="vmInputValue.length > 0"
      class="clear-button"
    >
      <FontAwesomeIcon
        icon="times"
        @click="clearInput"
      />
    </button>
    <div
      v-if="$slots.suffix"
      class="shrink-0"
    >
      <slot name="suffix" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.input-container {
  @apply flex gap-x-4 items-center w-full justify-between;

  .default-input {
    @apply block w-full border-0 bg-transparent outline-none hover:cursor-text focus:ring-0;

    &::-webkit-search-cancel-button {
      -webkit-appearance: none;
    }
  }

  .clear-button {
    @apply text-primary;
  }
}
</style>